import React, { useEffect, useState } from "react";
import { Breadcrumb, Layout, Card, Typography, Tabs, Form, Input, Button, Switch, InputNumber, Slider, Select, Divider, Space, notification, Spin } from "antd";
import { useAuth } from "../../hooks/useAuth";
import { FormattedMessage, useIntl } from "react-intl";
import { useGetSystemSettingsQuery, useUpdateSystemSettingsMutation } from "../../services/nestjs-base-api/admin-api";
import { SaveOutlined, SecurityScanOutlined, CloudOutlined, PictureOutlined, MailOutlined, ApiOutlined, SettingOutlined } from '@ant-design/icons';

const { Content } = Layout;
const { Title } = Typography;
const { TabPane } = Tabs;

interface ISystemSettings {
  general: {
    siteName: string;
    siteDescription: string;
    maintenanceMode: boolean;
    defaultLanguage: string;
  };
  security: {
    maxLoginAttempts: number;
    loginLockoutDuration: number;
    passwordMinLength: number;
    passwordRequireUppercase: boolean;
    passwordRequireNumbers: boolean;
    passwordRequireSpecialChars: boolean;
    sessionTimeoutMinutes: number;
    enableTwoFactorAuth: boolean;
  };
  storage: {
    maxUploadFileSize: number;
    allowedFileTypes: string[];
    storageProvider: string;
    compressionLevel: number;
    backupFrequency: string;
    backupRetentionDays: number;
  };
  recognition: {
    minDetectionConfidence: number;
    maxRecognitionResults: number;
    enableFaceBlurring: boolean;
    enableBatchProcessing: boolean;
    processingQueueSize: number;
  };
  email: {
    smtpServer: string;
    smtpPort: number;
    smtpUsername: string;
    smtpPassword: string;
    senderEmail: string;
    senderName: string;
    enableSsl: boolean;
  };
  api: {
    rateLimit: number;
    rateLimitWindow: number;
    enableCors: boolean;
    allowedOrigins: string;
    cacheTtl: number;
  };
}

const AdminSettingsPage: React.FunctionComponent = () => {
  const intl = useIntl();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState("general");
  
  // Forms for each tab
  const [generalForm] = Form.useForm();
  const [securityForm] = Form.useForm();
  const [storageForm] = Form.useForm();
  const [recognitionForm] = Form.useForm();
  const [emailForm] = Form.useForm();
  const [apiForm] = Form.useForm();
  
  // API hooks
  const { data, error, isLoading } = useGetSystemSettingsQuery({ 
    jwtToken: user.jwtToken 
  });
  const [updateSettings] = useUpdateSystemSettingsMutation();

  useEffect(() => {
    if (error) {
      if ('status' in error) {
        if (error.status === 401) {
          logout();
        }
      }
    }
  }, [error, logout]);

  // Initialize forms with data
  useEffect(() => {
    if (data) {
      // For demo purposes, we'll use mock data since the API doesn't exist yet
      const mockSettings: ISystemSettings = {
        general: {
          siteName: "RecogniX",
          siteDescription: "AI-powered facial recognition for events",
          maintenanceMode: false,
          defaultLanguage: "en",
        },
        security: {
          maxLoginAttempts: 5,
          loginLockoutDuration: 15,
          passwordMinLength: 8,
          passwordRequireUppercase: true,
          passwordRequireNumbers: true,
          passwordRequireSpecialChars: false,
          sessionTimeoutMinutes: 60,
          enableTwoFactorAuth: false,
        },
        storage: {
          maxUploadFileSize: 10,
          allowedFileTypes: ["jpg", "jpeg", "png"],
          storageProvider: "aws",
          compressionLevel: 85,
          backupFrequency: "daily",
          backupRetentionDays: 30,
        },
        recognition: {
          minDetectionConfidence: 0.7,
          maxRecognitionResults: 10,
          enableFaceBlurring: false,
          enableBatchProcessing: true,
          processingQueueSize: 50,
        },
        email: {
          smtpServer: "smtp.example.com",
          smtpPort: 587,
          smtpUsername: "noreply@recognix.com",
          smtpPassword: "********",
          senderEmail: "noreply@recognix.com",
          senderName: "RecogniX System",
          enableSsl: true,
        },
        api: {
          rateLimit: 100,
          rateLimitWindow: 60,
          enableCors: true,
          allowedOrigins: "*.recognix.com,localhost:*",
          cacheTtl: 300,
        },
      };
      
      // Set form values
      generalForm.setFieldsValue(mockSettings.general);
      securityForm.setFieldsValue(mockSettings.security);
      storageForm.setFieldsValue(mockSettings.storage);
      recognitionForm.setFieldsValue(mockSettings.recognition);
      emailForm.setFieldsValue(mockSettings.email);
      apiForm.setFieldsValue(mockSettings.api);
    }
  }, [data, generalForm, securityForm, storageForm, recognitionForm, emailForm, apiForm]);

  // Handle saving settings
  const handleSaveSettings = async (values: any, settingType: string) => {
    try {
      await updateSettings({
        jwtToken: user.jwtToken,
        payload: {
          type: settingType,
          settings: values,
        },
      }).unwrap();
      
      notification.success({
        message: intl.formatMessage({ id: 'admin.settings.saveSuccess' }) || 'Settings Saved',
        description: intl.formatMessage(
          { id: 'admin.settings.saveSuccessDesc' },
          { type: settingType.charAt(0).toUpperCase() + settingType.slice(1) }
        ) || `${settingType.charAt(0).toUpperCase() + settingType.slice(1)} settings have been successfully saved.`,
      });
    } catch (err) {
      notification.error({
        message: intl.formatMessage({ id: 'admin.settings.saveError' }) || 'Save Failed',
        description: intl.formatMessage({ id: 'admin.settings.saveErrorDesc' }) || 'Failed to save settings. Please try again.',
      });
    }
  };

  return (
    <Content style={{ margin: '0 16px' }}>
      <Breadcrumb style={{ margin: '16px 0' }}>
        <Breadcrumb.Item>RecogniX</Breadcrumb.Item>
        <Breadcrumb.Item>
          <FormattedMessage id={'section.adminDashboardName'} defaultMessage="Admin Dashboard" />
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <FormattedMessage id={'section.systemSettingsName'} defaultMessage="System Settings" />
        </Breadcrumb.Item>
      </Breadcrumb>
      
      <Card
        title={
          <Title level={2}>
            <FormattedMessage id={'admin.settings.title'} defaultMessage="System Settings" />
          </Title>
        }
      >
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <p style={{ marginTop: 16 }}>Loading system settings...</p>
          </div>
        ) : (
          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            {/* General Settings */}
            <TabPane 
              tab={
                <span>
                  <SettingOutlined />
                  <Space>
                    <FormattedMessage id={'admin.settings.general'} defaultMessage="General" />
                  </Space>
                </span>
              } 
              key="general"
            >
              <div style={{ position: 'relative', overflow: 'hidden' }}>
                {/* DRAFT Watermark */}
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%) rotate(-30deg)',
                    pointerEvents: 'none',
                    zIndex: 1,
                    opacity: 0.15,
                  }}
                >
                  <Typography.Text
                    style={{
                      fontSize: '100px',
                      fontWeight: 'bold',
                      color: '#ff4d4f',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    DRAFT
                  </Typography.Text>
                </div>

              <Form
                form={generalForm}
                layout="vertical"
                onFinish={(values) => handleSaveSettings(values, 'general')}
              >
                <Form.Item
                  name="siteName"
                  label={intl.formatMessage({ id: 'admin.settings.siteName' }) || "Site Name"}
                  rules={[{ required: true, message: 'Please enter site name' }]}
                >
                  <Input />
                </Form.Item>
                
                <Form.Item
                  name="siteDescription"
                  label={intl.formatMessage({ id: 'admin.settings.siteDescription' }) || "Site Description"}
                >
                  <Input.TextArea rows={3} />
                </Form.Item>
                
                <Form.Item
                  name="maintenanceMode"
                  label={intl.formatMessage({ id: 'admin.settings.maintenanceMode' }) || "Maintenance Mode"}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
                
                <Form.Item
                  name="defaultLanguage"
                  label={intl.formatMessage({ id: 'admin.settings.defaultLanguage' }) || "Default Language"}
                  rules={[{ required: true, message: 'Please select default language' }]}
                >
                  <Select>
                    <Select.Option value="en">English</Select.Option>
                    <Select.Option value="es">Spanish</Select.Option>
                    <Select.Option value="fr">French</Select.Option>
                    <Select.Option value="de">German</Select.Option>
                  </Select>
                </Form.Item>
                
                <Divider />
                
                <Form.Item>
                  <Button type="primary" htmlType="submit" icon={<SaveOutlined />} disabled={true}>
                    <Space>
                      <FormattedMessage id={'admin.settings.saveSettings'} defaultMessage="Save Settings"/>
                    </Space>
                  </Button>
                </Form.Item>
              </Form>
              </div> {/* Close watermark wrapper div */}
            </TabPane>

            {/* Security Settings */}
            <TabPane 
              tab={
                <span>
                  <SecurityScanOutlined />
                  <Space>
                    <FormattedMessage id={'admin.settings.security'} defaultMessage="Security" />
                  </Space>
                </span>
              } 
              key="security"
            >
              <div style={{ position: 'relative', overflow: 'hidden' }}>
                {/* DRAFT Watermark */}
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%) rotate(-30deg)',
                    pointerEvents: 'none',
                    zIndex: 1,
                    opacity: 0.15,
                  }}
                >
                  <Typography.Text
                    style={{
                      fontSize: '100px',
                      fontWeight: 'bold',
                      color: '#ff4d4f',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    DRAFT
                  </Typography.Text>
                </div>

              <Form
                form={securityForm}
                layout="vertical"
                onFinish={(values) => handleSaveSettings(values, 'security')}
              >
                <Title level={4}>
                  <FormattedMessage id={'admin.settings.loginSecurity'} defaultMessage="Login Security" />
                </Title>
                
                <Form.Item
                  name="maxLoginAttempts"
                  label={intl.formatMessage({ id: 'admin.settings.maxLoginAttempts' }) || "Max Login Attempts"}
                  rules={[{ required: true, message: 'Please enter max login attempts' }]}
                >
                  <InputNumber min={1} max={10} />
                </Form.Item>
                
                <Form.Item
                  name="loginLockoutDuration"
                  label={intl.formatMessage({ id: 'admin.settings.loginLockoutDuration' }) || "Login Lockout Duration (minutes)"}
                  rules={[{ required: true, message: 'Please enter lockout duration' }]}
                >
                  <InputNumber min={5} max={60} />
                </Form.Item>
                
                <Form.Item
                  name="enableTwoFactorAuth"
                  label={intl.formatMessage({ id: 'admin.settings.enableTwoFactorAuth' }) || "Enable Two-Factor Authentication"}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
                
                <Title level={4}>
                  <FormattedMessage id={'admin.settings.passwordPolicy'} defaultMessage="Password Policy" />
                </Title>
                
                <Form.Item
                  name="passwordMinLength"
                  label={intl.formatMessage({ id: 'admin.settings.passwordMinLength' }) || "Minimum Password Length"}
                  rules={[{ required: true, message: 'Please enter minimum password length' }]}
                >
                  <InputNumber min={6} max={16} />
                </Form.Item>
                
                <Form.Item
                  name="passwordRequireUppercase"
                  label={intl.formatMessage({ id: 'admin.settings.passwordRequireUppercase' }) || "Require Uppercase Letters"}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
                
                <Form.Item
                  name="passwordRequireNumbers"
                  label={intl.formatMessage({ id: 'admin.settings.passwordRequireNumbers' }) || "Require Numbers"}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
                
                <Form.Item
                  name="passwordRequireSpecialChars"
                  label={intl.formatMessage({ id: 'admin.settings.passwordRequireSpecialChars' }) || "Require Special Characters"}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
                
                <Title level={4}>
                  <FormattedMessage id={'admin.settings.sessionManagement'} defaultMessage="Session Management" />
                </Title>
                
                <Form.Item
                  name="sessionTimeoutMinutes"
                  label={intl.formatMessage({ id: 'admin.settings.sessionTimeoutMinutes' }) || "Session Timeout (minutes)"}
                  rules={[{ required: true, message: 'Please enter session timeout' }]}
                >
                  <InputNumber min={15} max={240} />
                </Form.Item>
                
                <Divider />
                
                <Form.Item>
                  <Button type="primary" htmlType="submit" icon={<SaveOutlined />} disabled={true}>
                    <Space>
                      <FormattedMessage id={'admin.settings.saveSettings'} defaultMessage="Save Settings" />
                    </Space>
                  </Button>
                </Form.Item>
              </Form>
              </div> {/* Close watermark wrapper div */}
            </TabPane>

            {/* Storage Settings */}
            <TabPane 
              tab={
                <span>
                  <CloudOutlined />
                  <Space>
                    <FormattedMessage id={'admin.settings.storage'} defaultMessage="Storage" />
                  </Space>
                </span>
              } 
              key="storage"
            >
              <div style={{ position: 'relative', overflow: 'hidden' }}>
                {/* DRAFT Watermark */}
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%) rotate(-30deg)',
                    pointerEvents: 'none',
                    zIndex: 1,
                    opacity: 0.15,
                  }}
                >
                  <Typography.Text
                    style={{
                      fontSize: '100px',
                      fontWeight: 'bold',
                      color: '#ff4d4f',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    DRAFT
                  </Typography.Text>
                </div>

              <Form
                form={storageForm}
                layout="vertical"
                onFinish={(values) => handleSaveSettings(values, 'storage')}
              >
                <Form.Item
                  name="maxUploadFileSize"
                  label={intl.formatMessage({ id: 'admin.settings.maxUploadFileSize' }) || "Max Upload File Size (MB)"}
                  rules={[{ required: true, message: 'Please enter max file size' }]}
                >
                  <InputNumber min={1} max={50} />
                </Form.Item>
                
                <Form.Item
                  name="allowedFileTypes"
                  label={intl.formatMessage({ id: 'admin.settings.allowedFileTypes' }) || "Allowed File Types"}
                  rules={[{ required: true, message: 'Please select allowed file types' }]}
                >
                  <Select mode="tags" placeholder="Enter file extensions">
                    <Select.Option value="jpg">jpg</Select.Option>
                    <Select.Option value="jpeg">jpeg</Select.Option>
                    <Select.Option value="png">png</Select.Option>
                    <Select.Option value="gif">gif</Select.Option>
                    <Select.Option value="webp">webp</Select.Option>
                  </Select>
                </Form.Item>
                
                <Form.Item
                  name="storageProvider"
                  label={intl.formatMessage({ id: 'admin.settings.storageProvider' }) || "Storage Provider"}
                  rules={[{ required: true, message: 'Please select storage provider' }]}
                >
                  <Select>
                    <Select.Option value="local">Local Storage</Select.Option>
                    <Select.Option value="aws">Amazon S3</Select.Option>
                    <Select.Option value="azure">Azure Blob Storage</Select.Option>
                    <Select.Option value="gcp">Google Cloud Storage</Select.Option>
                  </Select>
                </Form.Item>
                
                <Form.Item
                  name="compressionLevel"
                  label={intl.formatMessage({ id: 'admin.settings.compressionLevel' }) || "Image Compression Level (%)"}
                  rules={[{ required: true, message: 'Please enter compression level' }]}
                >
                  <Slider min={50} max={100} />
                </Form.Item>
                
                <Form.Item
                  name="backupFrequency"
                  label={intl.formatMessage({ id: 'admin.settings.backupFrequency' }) || "Backup Frequency"}
                  rules={[{ required: true, message: 'Please select backup frequency' }]}
                >
                  <Select>
                    <Select.Option value="hourly">Hourly</Select.Option>
                    <Select.Option value="daily">Daily</Select.Option>
                    <Select.Option value="weekly">Weekly</Select.Option>
                    <Select.Option value="monthly">Monthly</Select.Option>
                  </Select>
                </Form.Item>
                
                <Form.Item
                  name="backupRetentionDays"
                  label={intl.formatMessage({ id: 'admin.settings.backupRetentionDays' }) || "Backup Retention (days)"}
                  rules={[{ required: true, message: 'Please enter backup retention days' }]}
                >
                  <InputNumber min={1} max={365} />
                </Form.Item>
                
                <Divider />
                
                <Form.Item>
                  <Button type="primary" htmlType="submit" icon={<SaveOutlined />} disabled={true}>
                    <Space>
                      <FormattedMessage id={'admin.settings.saveSettings'} defaultMessage="Save Settings" />
                    </Space>
                  </Button>
                </Form.Item>
              </Form>
              </div> {/* Close watermark wrapper div */}
            </TabPane>

            {/* Recognition Settings */}
            <TabPane 
              tab={
                <span>
                  <PictureOutlined />
                  <Space>
                    <FormattedMessage id={'admin.settings.recognition'} defaultMessage="Recognition" />
                  </Space>
                </span>
              } 
              key="recognition"
            >
              <div style={{ position: 'relative', overflow: 'hidden' }}>
                {/* DRAFT Watermark */}
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%) rotate(-30deg)',
                    pointerEvents: 'none',
                    zIndex: 1,
                    opacity: 0.15,
                  }}
                >
                  <Typography.Text
                    style={{
                      fontSize: '100px',
                      fontWeight: 'bold',
                      color: '#ff4d4f',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    DRAFT
                  </Typography.Text>
                </div>

              <Form
                form={recognitionForm}
                layout="vertical"
                onFinish={(values) => handleSaveSettings(values, 'recognition')}
              >
                <Form.Item
                  name="minDetectionConfidence"
                  label={intl.formatMessage({ id: 'admin.settings.minDetectionConfidence' }) || "Minimum Detection Confidence"}
                  rules={[{ required: true, message: 'Please enter minimum detection confidence' }]}
                >
                  <Slider min={0.5} max={0.95} step={0.05} marks={{
                    0.5: '0.5',
                    0.7: '0.7',
                    0.95: '0.95'
                  }} />
                </Form.Item>
                
                <Form.Item
                  name="maxRecognitionResults"
                  label={intl.formatMessage({ id: 'admin.settings.maxRecognitionResults' }) || "Maximum Recognition Results"}
                  rules={[{ required: true, message: 'Please enter maximum recognition results' }]}
                >
                  <InputNumber min={1} max={50} />
                </Form.Item>
                
                <Form.Item
                  name="enableFaceBlurring"
                  label={intl.formatMessage({ id: 'admin.settings.enableFaceBlurring' }) || "Enable Face Blurring for Unmatched Faces"}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
                
                <Form.Item
                  name="enableBatchProcessing"
                  label={intl.formatMessage({ id: 'admin.settings.enableBatchProcessing' }) || "Enable Batch Processing"}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
                
                <Form.Item
                  name="processingQueueSize"
                  label={intl.formatMessage({ id: 'admin.settings.processingQueueSize' }) || "Processing Queue Size"}
                  rules={[{ required: true, message: 'Please enter processing queue size' }]}
                >
                  <InputNumber min={10} max={200} />
                </Form.Item>
                
                <Divider />
                
                <Form.Item>
                  <Button type="primary" htmlType="submit" icon={<SaveOutlined />} disabled={true}>
                    <Space>
                      <FormattedMessage id={'admin.settings.saveSettings'} defaultMessage="Save Settings" />
                    </Space>
                  </Button>
                </Form.Item>
              </Form>
              </div> {/* Close watermark wrapper div */}
            </TabPane>

            {/* Email Settings */}
            <TabPane 
              tab={
                <span>
                  <MailOutlined />
                  <Space>
                    <FormattedMessage id={'admin.settings.email'} defaultMessage="Email" />
                  </Space>
                </span>
              } 
              key="email"
            >
              <div style={{ position: 'relative', overflow: 'hidden' }}>
                {/* DRAFT Watermark */}
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%) rotate(-30deg)',
                    pointerEvents: 'none',
                    zIndex: 1,
                    opacity: 0.15,
                  }}
                >
                  <Typography.Text
                    style={{
                      fontSize: '100px',
                      fontWeight: 'bold',
                      color: '#ff4d4f',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    DRAFT
                  </Typography.Text>
                </div>

              <Form
                form={emailForm}
                layout="vertical"
                onFinish={(values) => handleSaveSettings(values, 'email')}
              >
                <Form.Item
                  name="smtpServer"
                  label={intl.formatMessage({ id: 'admin.settings.smtpServer' }) || "SMTP Server"}
                  rules={[{ required: true, message: 'Please enter SMTP server' }]}
                >
                  <Input />
                </Form.Item>
                
                <Form.Item
                  name="smtpPort"
                  label={intl.formatMessage({ id: 'admin.settings.smtpPort' }) || "SMTP Port"}
                  rules={[{ required: true, message: 'Please enter SMTP port' }]}
                >
                  <InputNumber min={1} max={65535} />
                </Form.Item>
                
                <Form.Item
                  name="smtpUsername"
                  label={intl.formatMessage({ id: 'admin.settings.smtpUsername' }) || "SMTP Username"}
                  rules={[{ required: true, message: 'Please enter SMTP username' }]}
                >
                  <Input />
                </Form.Item>
                
                <Form.Item
                  name="smtpPassword"
                  label={intl.formatMessage({ id: 'admin.settings.smtpPassword' }) || "SMTP Password"}
                  rules={[{ required: true, message: 'Please enter SMTP password' }]}
                >
                  <Input.Password />
                </Form.Item>
                
                <Form.Item
                  name="senderEmail"
                  label={intl.formatMessage({ id: 'admin.settings.senderEmail' }) || "Sender Email"}
                  rules={[
                    { required: true, message: 'Please enter sender email' },
                    { type: 'email', message: 'Please enter a valid email' }
                  ]}
                >
                  <Input />
                </Form.Item>
                
                <Form.Item
                  name="senderName"
                  label={intl.formatMessage({ id: 'admin.settings.senderName' }) || "Sender Name"}
                  rules={[{ required: true, message: 'Please enter sender name' }]}
                >
                  <Input />
                </Form.Item>
                
                <Form.Item
                  name="enableSsl"
                  label={intl.formatMessage({ id: 'admin.settings.enableSsl' }) || "Enable SSL/TLS"}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
                
                <Space>
                  <Button type="default">
                    <FormattedMessage id={'admin.settings.testEmailConnection'} defaultMessage="Test Connection" />
                  </Button>
                  <Button type="default">
                    <FormattedMessage id={'admin.settings.sendTestEmail'} defaultMessage="Send Test Email" />
                  </Button>
                </Space>
                
                <Divider />
                
                <Form.Item>
                  <Button type="primary" htmlType="submit" icon={<SaveOutlined />} disabled={true}>
                    <Space>
                      <FormattedMessage id={'admin.settings.saveSettings'} defaultMessage="Save Settings" />
                    </Space>
                  </Button>
                </Form.Item>
              </Form>
              </div> {/* Close watermark wrapper div */}
            </TabPane>

            {/* API Settings */}
            <TabPane 
              tab={
                <span>
                  <ApiOutlined />
                  <Space>
                    <FormattedMessage id={'admin.settings.api'} defaultMessage="API" />
                  </Space>
                </span>
              } 
              key="api"
            >
              <div style={{ position: 'relative', overflow: 'hidden' }}>
                {/* DRAFT Watermark */}
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%) rotate(-30deg)',
                    pointerEvents: 'none',
                    zIndex: 1,
                    opacity: 0.15,
                  }}
                >
                  <Typography.Text
                    style={{
                      fontSize: '100px',
                      fontWeight: 'bold',
                      color: '#ff4d4f',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    DRAFT
                  </Typography.Text>
                </div>

              <Form
                form={apiForm}
                layout="vertical"
                onFinish={(values) => handleSaveSettings(values, 'api')}
              >
                <Form.Item
                  name="rateLimit"
                  label={intl.formatMessage({ id: 'admin.settings.rateLimit' }) || "Rate Limit (requests)"}
                  rules={[{ required: true, message: 'Please enter rate limit' }]}
                >
                  <InputNumber min={10} max={1000} />
                </Form.Item>
                
                <Form.Item
                  name="rateLimitWindow"
                  label={intl.formatMessage({ id: 'admin.settings.rateLimitWindow' }) || "Rate Limit Window (seconds)"}
                  rules={[{ required: true, message: 'Please enter rate limit window' }]}
                >
                  <InputNumber min={10} max={3600} />
                </Form.Item>
                
                <Form.Item
                  name="enableCors"
                  label={intl.formatMessage({ id: 'admin.settings.enableCors' }) || "Enable CORS"}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
                
                <Form.Item
                  name="allowedOrigins"
                  label={intl.formatMessage({ id: 'admin.settings.allowedOrigins' }) || "Allowed Origins (comma-separated)"}
                >
                  <Input.TextArea rows={3} placeholder="*.example.com,localhost:*" />
                </Form.Item>
                
                <Form.Item
                  name="cacheTtl"
                  label={intl.formatMessage({ id: 'admin.settings.cacheTtl' }) || "Cache TTL (seconds)"}
                  rules={[{ required: true, message: 'Please enter cache TTL' }]}
                >
                  <InputNumber min={0} max={3600} />
                </Form.Item>
                
                <Divider />
                
                <Form.Item>
                  <Button type="primary" htmlType="submit" icon={<SaveOutlined />} disabled={true}>
                    <Space>
                      <FormattedMessage id={'admin.settings.saveSettings'} defaultMessage="Save Settings"/>
                    </Space>
                  </Button>
                </Form.Item>
              </Form>
              </div> {/* Close watermark wrapper div */}
            </TabPane>
          </Tabs>
        )}
      </Card>
    </Content>
  );
};

export default AdminSettingsPage;